/**
 * Notifications (design system, Notification) : messages brefs en bas au centre de la
 * fenêtre, trois au plus, la plus récente en bas. Sans action, elles s'effacent après 4 s
 * (décompte arrêté au survol et au focus) ; une erreur ou une action les garde.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { cx } from '../outils';
import { Bouton } from './Bouton';
import { Icone, type NomIcone } from './Icone';

export interface NotificationAEmettre {
  message: string;
  statut?: 'info' | 'succes' | 'danger';
  icone?: NomIcone;
  action?: { libelle: string; onAction: () => void | Promise<void> };
  /** Délai avant disparition (ms) ; `null` : reste jusqu'à sa fermeture. */
  duree?: number | null;
  /** Une notification de même clé remplace la précédente (« Carte ajoutée » successives). */
  cle?: string;
}

interface NotificationAffichee extends NotificationAEmettre {
  id: number;
  sortie: boolean;
}

type Notifier = ((notification: NotificationAEmettre) => void) & {
  /** Retire toutes les notifications (changement de contexte : début de séance). */
  effacer: () => void;
};

const Contexte = createContext<Notifier>(Object.assign(() => {}, { effacer: () => {} }));

export function useNotifier(): Notifier {
  return useContext(Contexte);
}

const ICONES = { info: 'info', succes: 'circle-check', danger: 'circle-alert' } as const;

function Notification({
  notification,
  onFermer,
}: {
  notification: NotificationAffichee;
  onFermer: () => void;
}) {
  const { message, statut = 'info', icone, action } = notification;
  const duree =
    notification.duree !== undefined
      ? notification.duree
      : action === undefined && statut !== 'danger'
        ? 4000
        : null;
  const [suspendu, setSuspendu] = useState(false);
  const [occupe, setOccupe] = useState(false);
  const restant = useRef(duree ?? 0);

  useEffect(() => {
    if (duree === null || suspendu || notification.sortie) return;
    const debut = Date.now();
    const minuteur = window.setTimeout(onFermer, restant.current);
    return () => {
      window.clearTimeout(minuteur);
      restant.current -= Date.now() - debut;
    };
  }, [duree, suspendu, notification.sortie, onFermer]);

  return (
    <div
      className={cx('fc-notification', statut !== 'info' && `fc-notification--${statut}`)}
      role={statut === 'danger' ? 'alert' : 'status'}
      data-etat={notification.sortie ? 'sortie' : undefined}
      onMouseEnter={() => setSuspendu(true)}
      onMouseLeave={() => setSuspendu(false)}
      onFocus={() => setSuspendu(true)}
      onBlur={() => setSuspendu(false)}
    >
      <Icone nom={icone ?? ICONES[statut]} className="fc-notification__icone" />
      <p className="fc-notification__message">{message}</p>
      {action !== undefined && (
        <Bouton
          variante="discret"
          taille="compacte"
          className="fc-notification__action"
          occupe={occupe}
          onClick={async () => {
            setOccupe(true);
            try {
              await action.onAction();
            } finally {
              setOccupe(false);
              onFermer();
            }
          }}
        >
          {action.libelle}
        </Bouton>
      )}
      <Bouton
        variante="discret"
        taille="compacte"
        iconeSeule
        icone="x"
        etiquette="Fermer la notification"
        title=""
        className="fc-notification__fermer"
        onClick={onFermer}
      />
    </div>
  );
}

export function FournisseurNotifications({ children }: { children: ReactNode }) {
  const [liste, setListe] = useState<NotificationAffichee[]>([]);
  const suivant = useRef(1);

  const fermer = useCallback((id: number) => {
    setListe((actuelles) => actuelles.map((n) => (n.id === id ? { ...n, sortie: true } : n)));
    window.setTimeout(() => setListe((actuelles) => actuelles.filter((n) => n.id !== id)), 160);
  }, []);

  const notifier = useMemo<Notifier>(
    () =>
      Object.assign(
        (notification: NotificationAEmettre) => {
          const id = suivant.current++;
          setListe((actuelles) => [
            ...actuelles
              .filter(
                (n) => !n.sortie && (notification.cle === undefined || n.cle !== notification.cle),
              )
              .slice(-2),
            { ...notification, id, sortie: false },
          ]);
        },
        { effacer: () => setListe([]) },
      ),
    [],
  );

  const fermetures = useMemo(() => new Map<number, () => void>(), []);
  const fermeture = (id: number) => {
    let f = fermetures.get(id);
    if (f === undefined) {
      f = () => fermer(id);
      fermetures.set(id, f);
    }
    return f;
  };

  return (
    <Contexte.Provider value={notifier}>
      {children}
      <div className="fc-notification__pile" role="region" aria-label="Notifications">
        {liste.map((notification) => (
          <Notification
            key={notification.id}
            notification={notification}
            onFermer={fermeture(notification.id)}
          />
        ))}
      </div>
    </Contexte.Provider>
  );
}
